"use client";

import type { Editor } from "@tiptap/react";
import {
	AlignCenterVertical,
	AlignEndVertical,
	AlignStartVertical,
	Columns,
	Merge,
	Plus,
	Rows,
	Settings,
	SplitSquareVertical,
	Table as TableIcon,
	TextAlignCenter,
	TextAlignEnd,
	TextAlignJustify,
	TextAlignStart,
	Trash2,
} from "lucide-react";
import React from "react";
import type { SelectionState } from "../types";
import { applyAlignmentToSelectedCells, applyTableBorderToCells } from "../utils/tableCellOps";

interface TableToolbarProps {
	editor: Editor | null;
	selectionState: SelectionState;
	onSelectionStateChange: (state: Partial<SelectionState>) => void;
	onOpenUnifiedTableSettings: () => void;
	onDeleteTable?: () => void;
}

export const TableToolbar: React.FC<TableToolbarProps> = ({
	editor,
	selectionState,
	onSelectionStateChange,
	onOpenUnifiedTableSettings,
	onDeleteTable,
}) => {
	if (!selectionState.isInTable) return null;

	return (
		<div className="flex flex-wrap gap-2 p-2 border-b bg-gradient-to-r from-blue-50 to-indigo-50 items-center">
			<span className="text-sm font-semibold text-gray-700 flex items-center gap-1">
				<TableIcon className="w-4 h-4" />
				Table Tools:
			</span>

			{/* Row Operations */}
			<div className="flex items-center gap-1 border-r border-gray-300 pr-2">
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						if (!editor) return;
						const tableId = editor.getAttributes("table")["data-table-id"] as string | undefined;
						editor.chain().focus().addRowBefore().run();
						// TipTap creates the new cells with no border attrs at all
						setTimeout(() => applyTableBorderToCells(editor, tableId), 0);
					}}
					className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center gap-1"
					title="Add Row Above"
				>
					<Plus className="w-3 h-3" />
					<Rows className="w-3 h-3" />↑
				</button>
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						if (!editor) return;
						const tableId = editor.getAttributes("table")["data-table-id"] as string | undefined;
						editor.chain().focus().addRowAfter().run();
						setTimeout(() => applyTableBorderToCells(editor, tableId), 0);
					}}
					className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center gap-1"
					title="Add Row Below"
				>
					<Plus className="w-3 h-3" />
					<Rows className="w-3 h-3" />↓
				</button>
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						if (!editor) return;
						const tableId = editor.getAttributes("table")["data-table-id"] as string | undefined;
						editor.chain().focus().deleteRow().run();
						// Deleting the first row leaves the new first row without a top border
						setTimeout(() => applyTableBorderToCells(editor, tableId), 0);
					}}
					className="px-2 py-1 text-xs border border-red-300 rounded bg-white hover:bg-red-50 text-red-600 flex items-center gap-1"
					title="Delete Row"
				>
					<Trash2 className="w-3 h-3" />
					<Rows className="w-3 h-3" />
				</button>
			</div>

			{/* Column Operations */}
			<div className="flex items-center gap-1 border-r border-gray-300 pr-2">
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						if (!editor) return;
						const tableId = editor.getAttributes("table")["data-table-id"] as string | undefined;
						editor.chain().focus().addColumnBefore().run();
						// Also re-numbers data-column-index, which the column CSS keys off
						setTimeout(() => applyTableBorderToCells(editor, tableId), 0);
					}}
					className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center gap-1"
					title="Add Column Left"
				>
					<Plus className="w-3 h-3" />
					<Columns className="w-3 h-3" />←
				</button>
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						if (!editor) return;
						const tableId = editor.getAttributes("table")["data-table-id"] as string | undefined;
						editor.chain().focus().addColumnAfter().run();
						setTimeout(() => applyTableBorderToCells(editor, tableId), 0);
					}}
					className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center gap-1"
					title="Add Column Right"
				>
					<Plus className="w-3 h-3" />
					<Columns className="w-3 h-3" />→
				</button>
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						if (!editor) return;
						const tableId = editor.getAttributes("table")["data-table-id"] as string | undefined;
						editor.chain().focus().deleteColumn().run();
						setTimeout(() => applyTableBorderToCells(editor, tableId), 0);
					}}
					className="px-2 py-1 text-xs border border-red-300 rounded bg-white hover:bg-red-50 text-red-600 flex items-center gap-1"
					title="Delete Column"
				>
					<Trash2 className="w-3 h-3" />
					<Columns className="w-3 h-3" />
				</button>
			</div>

			{/* Merge/Split Operations */}
			<div className="flex items-center gap-1 border-r border-gray-300 pr-2">
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						editor?.chain().focus().mergeCells().run();
						setTimeout(() => {
							onSelectionStateChange({
								canMergeCells: editor?.can().mergeCells() || false,
								canSplitCell: editor?.can().splitCell() || false,
							});
						}, 0);
					}}
					disabled={!selectionState.canMergeCells}
					className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
					title="Merge Cells (select multiple cells first)"
				>
					<Merge className="w-3 h-3" />
					Merge
				</button>
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						editor?.chain().focus().splitCell().run();
						setTimeout(() => {
							onSelectionStateChange({
								canMergeCells: editor?.can().mergeCells() || false,
								canSplitCell: editor?.can().splitCell() || false,
							});
						}, 0);
					}}
					disabled={!selectionState.canSplitCell}
					className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
					title="Split Cell"
				>
					<SplitSquareVertical className="w-3 h-3" />
					Split
				</button>
			</div>

			{/* Header Toggle */}
			<div className="flex items-center gap-1 border-r border-gray-300 pr-2">
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						editor?.chain().focus().toggleHeaderRow().run();
					}}
					className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center gap-1"
					title="Toggle Header Row"
				>
					Header Row
				</button>
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						editor?.chain().focus().toggleHeaderColumn().run();
					}}
					className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center gap-1"
					title="Toggle Header Column"
				>
					Header Col
				</button>
			</div>

			{/* Cell Selection Help */}
			<div className="flex items-center gap-1 border-r border-gray-300 pr-2">
				<div className="px-2 py-1 text-xs bg-yellow-50 border border-yellow-300 rounded text-yellow-800 flex items-center gap-1" title="To select cells for styling: Click and drag across cells, or Shift+Click">
					💡 Tip: Drag to select cells
				</div>
			</div>

			{/* Cell Alignment */}
			<div className="flex items-center gap-1 border-r border-gray-300 pr-2">
				<span className="text-xs text-gray-500 shrink-0">Text:</span>
				<button
					type="button"
					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (editor) applyAlignmentToSelectedCells(editor, "textAlign", "left"); }}
					className="px-1.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center"
					title="Align Text Left"
				>
					<TextAlignStart className="w-3.5 h-3.5" />
				</button>
				<button
					type="button"
					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (editor) applyAlignmentToSelectedCells(editor, "textAlign", "center"); }}
					className="px-1.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center"
					title="Align Text Center"
				>
					<TextAlignCenter className="w-3.5 h-3.5" />
				</button>
				<button
					type="button"
					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (editor) applyAlignmentToSelectedCells(editor, "textAlign", "right"); }}
					className="px-1.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center"
					title="Align Text Right"
				>
					<TextAlignEnd className="w-3.5 h-3.5" />
				</button>
				<button
					type="button"
					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (editor) applyAlignmentToSelectedCells(editor, "textAlign", "justify"); }}
					className="px-1.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center"
					title="Justify Text"
				>
					<TextAlignJustify className="w-3.5 h-3.5" />
				</button>
				<span className="text-xs text-gray-500 shrink-0 ml-1">Vert:</span>
				<button
					type="button"
					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (editor) applyAlignmentToSelectedCells(editor, "verticalAlign", "top"); }}
					className="px-1.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center"
					title="Vertical Align Top"
				>
					<AlignStartVertical className="w-3.5 h-3.5" />
				</button>
				<button
					type="button"
					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (editor) applyAlignmentToSelectedCells(editor, "verticalAlign", "middle"); }}
					className="px-1.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center"
					title="Vertical Align Middle"
				>
					<AlignCenterVertical className="w-3.5 h-3.5" />
				</button>
				<button
					type="button"
					onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (editor) applyAlignmentToSelectedCells(editor, "verticalAlign", "bottom"); }}
					className="px-1.5 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 flex items-center"
					title="Vertical Align Bottom"
				>
					<AlignEndVertical className="w-3.5 h-3.5" />
				</button>
			</div>

			{/* Table Settings - Opens unified settings modal */}
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onOpenUnifiedTableSettings();
				}}
				className="px-3 py-1.5 text-xs border border-blue-400 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 flex items-center gap-1 font-medium"
				title="Table Settings - Configure table, column, and cell styles. To style specific cells: First drag to select cells, then click this button."
			>
				<Settings className="w-4 h-4" />
				Table Settings
			</button>

			{/* Delete Table */}
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					if (confirm("Are you sure you want to delete this table?")) {
						if (onDeleteTable) {
							onDeleteTable();
						} else {
							editor?.chain().focus().deleteTable().run();
						}
					}
				}}
				className="px-2 py-1 text-xs border border-red-400 rounded bg-red-50 hover:bg-red-100 text-red-600 flex items-center gap-1"
				title="Delete Table"
			>
				<Trash2 className="w-3 h-3" />
				Delete Table
			</button>
		</div>
	);
};


